<template>
  <div class = 'home-wrapper'>

    <!-- Loading Error -->
    <v-snackbar v-if="loadError" type="error">{{ loadError }}</v-snackbar>

    <h1 v-if="stage">Book an appointment</h1>

    <div class = 'progress-indicator' v-if="stage">
      <div v-bind:class = "{'progress-indicator-selected': [null, 'start', 'personal'].indexOf(stage) != -1 }">
        <span>1</span>
      </div>
      <div>
        <hr/>
      </div>
      <div v-bind:class = "{'progress-indicator-selected': ['treatment_category', 'client_type', 'treatment', 'name', 'preferred_dates', 'number_of_people', 'additional_info'].indexOf(stage) != -1 }">
        <span>2</span>
      </div>
      <div>
        <hr/>
      </div>
      <div v-bind:class = "{'progress-indicator-selected': ['confirm_verification', 'verification', 'thank_you'].indexOf(stage) != -1 }">
        <span>3</span>
      </div>
    </div>

    <!-- STAGE: START -->
    <div class = 'stage-item' v-if="stage=='start'">

      <div>

        <div class = 'section-descriptor'>
          <div>
            <v-icon>mdi-map-marker</v-icon>
          </div>
          <div>
            <h2>Where do you want your appointment?</h2>
          </div>
        </div>

        <div class = 'section-container'>

          <div v-bind:class = "{ 'form-field': true, 'form-field-error': isFormFieldError('address_string'), 'address_string': true }">
            <GoogleAddressAutocomplete 
              ref="addressInput"
              :api-key="googleMapsApiKey"
              v-model="address_lookup"
              @callback="addressCallback"    
              placeholder="Enter address"
              class="client-location-address-input"
            />

          </div>

          <div v-bind:class = "{ 'form-field': true, 'form-field-error': isFormFieldError('unit_number'), 'unit_number': true }">
            <v-text-field
              v-model="draft_booking.unit_number"
              placeholder="Unit / Suite"
              variant="outlined"
              hide-details
            ></v-text-field>
          </div>

        </div>

        <div class = 'form-error' v-if='form_error'>
          {{ form_error }}
        </div>

      </div>  

      <div class = 'button-wrapper center'>
        <v-btn @click="goToNextStage" :disabled="updateUnderway" variant="tonal">Next</v-btn>
      </div>

    </div>

    <!-- STAGE: PERSONAL -->
    <div class = 'stage-item' v-else-if="stage=='personal'">

      <div>

        <div class = 'section-descriptor'>
          <div>
            <v-icon>mdi-account</v-icon>
          </div>
          <div>
            <h2>Please enter your personal information.</h2>
          </div>
        </div>

        <div class = 'section-container'>

          <div v-bind:class = "{ 'form-field': true, 'form-field-error': isFormFieldError('email'), 'email': true }">
            <v-text-field
              v-model="draft_booking.email"
              placeholder="Enter email address"
              variant="outlined"
              hide-details
            ></v-text-field>
          </div>

          <div v-bind:class = "{ 'form-field': true, 'form-field-error': isFormFieldError('re_enter_email'), 're_enter_email': true }">
            <v-text-field
              placeholder="Re-enter email address"
              v-model="draft_booking.re_enter_email"
              variant="outlined"
              hide-details
            ></v-text-field>
          </div>

        </div>


        <div class = 'form-error' v-if='form_error'>
          {{ form_error }}
        </div>

      </div>

      <div class = 'button-wrapper'>
        <v-btn @click="goToPreviousStage()" v-if="hasGoBack()" :disabled="updateUnderway" variant="outlined">Back</v-btn>
        <v-btn @click="goToNextStage()" :disabled="updateUnderway" variant="tonal">Next</v-btn>
      </div>

    </div>

    <!-- STAGE: TREATMENT CATEGORY -->
    <div class = 'stage-item' v-else-if="stage=='treatment_category'">

      <div>

        <v-overlay
          v-model="showTreatmentCategoryNotAvailableOverlay"
          class="align-center justify-center error-overlay"
          contained
        >

          <div class = 'error-overlay-content'>

            <div class = 'sad-face'>
              <v-icon>mdi-emoticon-sad-outline</v-icon>
            </div>

            <h2>Oh no!</h2>

            <div class = 'error-overlay-description'>
              Looks like this service is not offered in your area yet.
            </div>

            <div class = 'error-overlay-description'>
              We'll let you know as soon as your area supports this service.
            </div>              

            <div class = 'button-wrapper center'>
              <v-btn @click="showTreatmentCategoryNotAvailableOverlay = false" variant="tonal">Close</v-btn>
            </div>

          </div>

        </v-overlay>

        <div class = 'section-descriptor'>
          <div>
            <v-icon>mdi-heart</v-icon>
          </div>
          <div>
            <h2>What service would you like?</h2>
          </div>
        </div>

        <div class = 'section-container'>

          <div v-bind:class = "{ 'form-field': true, 'form-field-error': isFormFieldError('treatment_category_id'), 'treatment_category_id': true }">
            <v-select
              placeholder="Select"
              v-model="draft_booking.treatment_category_id"
              :items="all_treatment_categories"
              item-title="display_name"
              item-value="treatment_category_id"        
              variant="outlined"      
              hide-details
            ></v-select>
          </div>

          <div class = 'treatment_need_help'>
            <p>Need help deciding which service you need? <a href = 'mailto:info@healthcasa.com' target = '_new'>Email us</a> or <a href = 'sms:+16476915150' target = '_new'>Text us</a>.</p>
          </div>

        </div>

        <div class = 'form-error' v-if='form_error'>
          {{ form_error }}
        </div>

      </div>

      <div class = 'button-wrapper'>
        <v-btn @click="goToPreviousStage()" v-if="hasGoBack()" :disabled="updateUnderway" variant="outlined">Back</v-btn>
        <v-btn @click="goToNextStage()" :disabled="updateUnderway" variant="tonal">Next</v-btn>
      </div>

    </div>

    <!-- STAGE: CLIENT TYPE -->
    <div class = 'stage-item' v-else-if="stage=='client_type'">

      <div>

        <div class = 'section-descriptor'>
          <div>
            <v-icon>mdi-heart</v-icon>
          </div>
          <div>
            <h2>Is this your first HealthCasa {{ getSelectedTreatmentCategoryDisplayName() }} appointment?</h2>
          </div>
        </div>          

        <div class = 'section-container'>

          <div v-bind:class = "{ 'form-field': true, 'form-field-error': isFormFieldError('client_type'), 'client_type': true }">
            <v-radio-group v-model="draft_booking.client_type" hide-details>
              <v-radio hide-details label="Yes" value="new"></v-radio>
              <v-radio hide-details label="No" value="existing"></v-radio>
            </v-radio-group>
          </div>

        </div>

        <div class = 'form-error' v-if='form_error'>
          {{ form_error }}
        </div>          

      </div>

      <div class = 'button-wrapper'>
        <v-btn @click="goToPreviousStage()" v-if="hasGoBack()" :disabled="updateUnderway" variant="outlined">Back</v-btn>
        <v-btn @click="goToNextStage()" :disabled="updateUnderway" variant="tonal">Next</v-btn>
      </div>

    </div>

    <!-- STAGE: TREATMENT -->
    <div class = 'stage-item' v-else-if="stage=='treatment'">

      <div>

        <div class = 'section-descriptor'>
          <div>
            <v-icon>mdi-heart</v-icon>
          </div>
          <div>
            <h2>Please select the desired treatment type.</h2>
          </div>
        </div>          

        <div class = 'section-container'>

          <div class = 'form-field treatment-list'>

            <v-radio-group v-model="draft_booking.treatment_id" hide-details>
              <div v-for = "treatment in treatments" :key="treatment.treatment_id">            
                <div>
                  <v-radio hide-details :label="treatment.display_name + (treatment.show_duration ? ' - ' + treatment.duration_minutes + ' min' : '')" :value="treatment.treatment_id"></v-radio>
                </div>
                <div style = 'margin-left:40px'>
                  ${{ treatment.price }} {{ treatment.hst_applies ? '+ Tax' : '' }}
                </div>
              </div>              
            </v-radio-group>

          </div>

        </div>

        <div class = 'form-error' v-if='form_error'>
          {{ form_error }}
        </div>

      </div>

      <div class = 'button-wrapper'>
        <v-btn @click="goToPreviousStage()" v-if="hasGoBack()" :disabled="updateUnderway" variant="outlined">Back</v-btn>
        <v-btn @click="goToNextStage()" :disabled="updateUnderway" variant="tonal">Next</v-btn>
      </div>


    </div>

    <!-- STAGE: NAME -->
    <div class = 'stage-item' v-else-if="stage=='name'">

      <div>

        <div class = 'section-descriptor'>
          <div>
            <v-icon>mdi-heart</v-icon>
          </div>
          <div>
            <h2>Please enter your first and last name and contact information.</h2>
          </div>
        </div>

        <div class = 'section-container'>

          <div v-bind:class = "{ 'form-field': true, 'form-field-error': isFormFieldError('first_name'), 'first_name': true }">
            <v-text-field
              placeholder="First Name"
              v-model="draft_booking.first_name"
              variant="outlined"
              hide-details
            ></v-text-field>
          </div>

          <div v-bind:class = "{ 'form-field': true, 'form-field-error': isFormFieldError('last_name'), 'last_name': true }">
            <v-text-field
              placeholder="Last Name"
              v-model="draft_booking.last_name"
              variant="outlined"
              hide-details
            ></v-text-field>
          </div>

          <div v-bind:class = "{ 'form-field': true, 'form-field-error': isFormFieldError('phone_number'), 'phone_number': true }">
            <v-text-field
              v-model="draft_booking.phone_number"
              placeholder="Enter phone number e.g. +12345678901 - Canadian Numbers Only"
              variant="outlined"
              hide-details
            ></v-text-field>
          </div>

          <div class = 'section-descriptor'>
            <div>
              <v-icon>mdi-account</v-icon>
            </div>
            <div>
              <h2>How do you prefer to be contacted?</h2>
            </div>
          </div>

          <div class = 'section-container'>

            <div v-bind:class = "{ 'form-field': true, 'form-field-error': isFormFieldError('primary_contact_method'), 'primary_contact_method': true }">
              <v-radio-group v-model="draft_booking.primary_contact_method">
                <v-radio label="Mobile Phone" value="phone_number"></v-radio>
                <v-radio label="Email" value="email"></v-radio>
              </v-radio-group>
            </div>

          </div>              


        </div>

        <div class = 'form-error' v-if='form_error'>
          {{ form_error }}
        </div>

      </div>

      <div class = 'button-wrapper'>
        <v-btn @click="goToPreviousStage()" v-if="hasGoBack()" :disabled="updateUnderway" variant="outlined">Back</v-btn>
        <v-btn @click="goToNextStage()" :disabled="updateUnderway" variant="tonal">Next</v-btn>
      </div>

    </div>

    <!-- STAGE: PREFERRED DATES -->
    <div class = 'stage-item' v-else-if="stage=='preferred_dates'">

      <div>

        <div class = 'section-descriptor'>
          <div>
            <v-icon>mdi-heart</v-icon>
          </div>
          <div>
            <h2>When do you want your appointment?</h2>
          </div>
        </div>

        <div class = 'section-container'>

          <div 
              v-for="(scheduling_preferences_by_day, index) in draft_booking.scheduling_preferences_by_day" :key="scheduling_preferences_by_day"
              v-bind:class = "{ 'form-field': true, 'form-field-error': isFormFieldError('scheduling_preferences_by_day_' + index), 'scheduling_preferences_by_day_item': true }"
              >
            <div>
              <div>
                <v-icon>mdi-calendar-account-outline</v-icon>
              </div>
              <div>
                {{ scheduling_preferences_by_day.date_format_nice }}
              </div>
              <div>
                <v-icon @click="removePreferredDate(index)">mdi-close</v-icon>
              </div>
            </div>
            <div>
              <!-- can select one or more as toggle buttons -->
              <div v-for="allowed_scheduling_preference in allowed_scheduling_preferences_per_day" :key="allowed_scheduling_preference" :value="allowed_scheduling_preference"
              v-bind:class="{ 'preferred-date-button': true, 'preferred-date-button-selected': scheduling_preferences_by_day.scheduling_preferences.includes(allowed_scheduling_preference) }">
                <v-btn :variant="scheduling_preferences_by_day.scheduling_preferences.includes(allowed_scheduling_preference) ? 'tonal' : 'outlined'"
                  @click="toggleSchedulingPreference(scheduling_preferences_by_day, allowed_scheduling_preference)">
                  {{ allowed_scheduling_preference }}
                </v-btn>
              </div>
            </div>
          </div>

          <div class = 'scheduling_preferences_by_day_add_date' v-if="!preferred_dates_is_add_mode&&canAddAnotherPreferredDate()"  @click="enablePreferredDatesAddDate()">
            <div>
              <v-icon>mdi-calendar-account-outline</v-icon>
            </div>
            <div>
              Add Date
            </div>
            <div>
              <v-icon class="add-icon">mdi-plus</v-icon>
            </div>
          </div>


          <div v-if="preferred_dates_is_add_mode">
            <VDatePicker id='preferred_dates_add_date' 
                  v-model="preferred_dates_add_date" 
                  view="monthly"
                  :min-date="selectable_preferred_service_dates[0]"
                  :max-date="selectable_preferred_service_dates[selectable_preferred_service_dates.length - 1]"                  
                  expanded
                />
              <div class = 'preferred-date-add-scheduling-preference'>
                <div v-for="allowed_scheduling_preference in allowed_scheduling_preferences_per_day" :key="allowed_scheduling_preference" :value="allowed_scheduling_preference"
                  v-bind:class="{ 'preferred-date-button': true, 'preferred-date-button-selected': preferred_dates_add_scheduling_preference.includes(allowed_scheduling_preference) }">
                  <v-btn :variant="preferred_dates_add_scheduling_preference.includes(allowed_scheduling_preference) ? 'tonal' : 'outlined'"
                    @click="toggleSchedulingPreferenceAdd(allowed_scheduling_preference)">
                    {{ allowed_scheduling_preference }}
                  </v-btn>
                </div>
            </div>

          </div>

        </div>

        

        <div class = 'form-error' v-if='form_error'>
          {{ form_error }}
        </div>

      </div>

      <div class = 'button-wrapper' v-if="preferred_dates_is_add_mode">
        <v-btn @click="preferred_dates_is_add_mode=false" variant="outlined">Back</v-btn>
        <v-btn @click="addPreferredDate()" :disabled="updateUnderway" variant="tonal">Add Date</v-btn>
      </div>
      <div class = 'button-wrapper' v-if="!preferred_dates_is_add_mode">
        <v-btn @click="goToPreviousStage()" v-if="hasGoBack()" variant="outlined">Back</v-btn>
        <v-btn @click="goToNextStage()" :disabled="updateUnderway" variant="tonal">Next</v-btn>
      </div>          
    </div>

    <!-- STAGE: NUMBER OF PEOPLE -->
    <div class = 'stage-item' v-else-if="stage=='number_of_people'">

      <div>

        <div class = 'section-descriptor'>
          <div>
            <v-icon>mdi-heart</v-icon>
          </div>
          <div>
            <h2>How many people are you booking for?</h2>
          </div>
        </div>

        <div class = 'section-container'>

          <div v-bind:class = "{ 'form-field': true, 'form-field-error': isFormFieldError('number_of_people'), 'number_of_people': true }">
            <v-radio-group v-model="draft_booking.number_of_people">
              <v-radio label="1" :value="1"></v-radio>
              <v-radio label="2" :value="2"></v-radio>
              <v-radio label="3+" :value="3"></v-radio>
            </v-radio-group>
          </div>

        </div>

        <div class = 'form-error' v-if='form_error'>
          {{ form_error }}
        </div>

      </div>

      <div class = 'button-wrapper'>
        <v-btn @click="goToPreviousStage()" v-if="hasGoBack()" :disabled="updateUnderway" variant="outlined">Back</v-btn>
        <v-btn @click="goToNextStage()" :disabled="updateUnderway" variant="tonal">Next</v-btn>
      </div>

    </div>

    <!-- STAGE: ADDITIONAL INFO -->
    <div class = 'stage-item' v-else-if="stage=='additional_info'">

      <div>

        <div class = 'section-descriptor'>
          <div>
            <v-icon>mdi-heart</v-icon>
          </div>
          <div>
            <h2>Reason for visit? (Required)</h2>
          </div>
        </div>     

        <div class = 'section-container'>

          <div v-bind:class = "{ 'form-field': true, 'form-field-error': isFormFieldError('reason_for_visit'), 'reason_for_visit': true }">
            <v-text-field
              placeholder="What condition/injury/etc. do you need help with?"
              v-model="draft_booking.reason_for_visit"
              variant="outlined"
              hide-details
            ></v-text-field>
          </div>            

        </div>

        <div class = 'section-descriptor'>
          <div>
            <v-icon>mdi-heart</v-icon>
          </div>
          <div>
            <h2>Do you have a promo code or additional notes? (Optional)</h2>
          </div>
        </div>          

        <div class = 'section-container'>

          <div v-bind:class = "{ 'form-field': true, 'form-field-error': isFormFieldError('promo_code'), 'promo_code': true }">
            <v-text-field
              placeholder="Enter promo code"
              v-model="draft_booking.promo_code"
              variant="outlined"
              hide-details
            ></v-text-field>
          </div>

          <div v-bind:class = "{ 'form-field': true, 'form-field-error': isFormFieldError('client_notes'), 'client_notes': true }">
            <v-textarea
              placeholder="Additional notes"
              v-model="draft_booking.client_notes"
              variant="outlined"
              hide-details
            ></v-textarea>
          </div>

        </div>

        <div class = 'form-error' v-if='form_error'>
          {{ form_error }}
        </div>

      </div>

      <div class = 'button-wrapper'>
        <v-btn @click="goToPreviousStage()" v-if="hasGoBack()" :disabled="updateUnderway" variant="outlined">Back</v-btn>
        <v-btn @click="goToNextStage()" :disabled="updateUnderway" variant="tonal">Next</v-btn>
      </div>

    </div>

    <!-- STAGE: CONFIRM VERIFICATION -->
    <div class = 'stage-item' v-else-if="stage=='confirm_verification'">

      <div>

        <div class = 'confirm_verification_content'>

          <div class = 'confirm_verification_content_icon'>
            <v-icon>mdi-account</v-icon>
          </div>
          <div class = 'confirm_verification_content_title'>
            Almost there!
          </div>
          <div class = 'confirm_verification_content_text'>
            We need to verify your contact information.
          </div>
          <div class = 'confirm_verification_content_text'>
            Click the button below and we'll send you a 6-digit verification code.
          </div>
          <div  class = 'confirm_verification_content_trouble'>
            Having trouble? <a href="mailto:info@healthcasa.com" target = '_new'>Email us</a> or <a href="sms:+16476915150" target = '_new'>Text us</a>.
          </div>
        </div>

        <div class = 'form-error' v-if='form_error'>
          {{ form_error }}
        </div>

      </div>

      <div class = 'button-wrapper dontfixtobottom'>
        <v-btn @click="goToPreviousStage()" v-if="hasGoBack()" :disabled="updateUnderway" variant="outlined">Back</v-btn>
        <v-btn @click="goToNextStage()" :disabled="updateUnderway" variant="tonal">Send Code</v-btn>
      </div>

    </div>

    <!-- STAGE: VERIFICATION -->
    <div class = 'stage-item' v-else-if="stage=='verification'">

      <div>

        <div class = 'section-descriptor'>
          <div>
            <v-icon>mdi-account</v-icon>
          </div>
          <div>
            <h2>Please enter the 6-digit verification code sent to both your phone number ending in {{ draft_booking.phone_number.slice(-4) }} and email @{{ draft_booking.email.split('@')[1] }}.</h2>
          </div>
        </div>          

        <div class = 'section-container'>

          <div v-bind:class = "{ 'form-field': true, 'form-field-error': isFormFieldError('verification_code'), 'verification_code': true }">
            <v-otp-input
              :length="verification_code_length"
              v-model="draft_booking.verification_code"
              variant="outlined"
            ></v-otp-input>

          </div>

        </div>

        <div class = 'form-error' v-if="is_invalid_verification_code">
          Incorrect code. Please try again.
        </div>      

        <div v-if="!is_invalid_verification_code" v-bind:class = "{ 'form-field': true, 'form-field-error': isFormFieldError('agreed_to_terms'), 'agreed_to_terms': true }">
          <v-checkbox
            v-model="draft_booking.agreed_to_terms"
          >
            <template v-slot:label>
              <div class = 'agreed_to_terms_label'>
                I agree to the 
                <a 
                  :href="`https://www.healthcasa.com/terms-and-conditions${utm_source || utm_medium || utm_campaign ? `?${[
                    utm_source ? `utm_source=${encodeURIComponent(utm_source)}` : '',
                    utm_medium ? `utm_medium=${encodeURIComponent(utm_medium)}` : '',
                    utm_campaign ? `utm_campaign=${encodeURIComponent(utm_campaign)}` : ''
                  ].filter(Boolean).join('&')}` : ''}`" 
                  target="_new"
                >
                  terms and conditions
                </a>
              </div>
            </template>
          </v-checkbox>
          
        </div>

        <div class = 'form-error' v-if="!is_invalid_verification_code">
          {{ form_error }}
        </div>

  

        <div class = 'invalid-verification-code' v-if="is_invalid_verification_code">
            <div>
              Didn't get a code? <a href = 'javascript:void(0)' @click="sendVerificationCode()">Click to resend</a>
            </div>
            <div>
              <a href = 'javascript:void(0)' @click="goToStart()">Edit contact information</a>
            </div>
        </div>

        <div class = 'verification-trouble'>
          Having trouble? <a href="mailto:info@healthcasa.com" target = '_new'>Email us</a> or <a href="sms:+16476915150" target = '_new'>Text us</a>.
        </div>

      </div>

      <div class = 'button-wrapper'>
        <v-btn @click="goToPreviousStage()" v-if="hasGoBack()" :disabled="updateUnderway" variant="outlined">Back</v-btn>
        <v-btn @click="goToNextStage()" :disabled="updateUnderway" variant="tonal">Verify</v-btn>
      </div>

    </div>

    <!-- STAGE: THANK YOU -->
    <div class = 'stage-item' v-else-if="stage=='thank_you'">

      <div>

        <div class = 'thank_you_content'>

          <div class = 'thank_you_content_icon'>
            <v-icon>mdi-check-circle</v-icon>
          </div>
          <div class = 'thank_you_content_title'>
            Success!
          </div>
          <div class = 'thank_you_content_text'>
            Thanks for submitting your appointment request.
          </div>
          <div class = 'thank_you_content_text'>
            We'll be in touch shortly, via your preferred contact method, with some availability options.
          </div>
          <div class = 'thank_you_content_text'>
            Please check your junk/spam folder in case it goes there.
          </div>
        </div>


      </div>

      <div class = 'button-wrapper center'>
        <v-btn @click="startAgain()" :disabled="updateUnderway" variant="tonal">Start Again</v-btn>
      </div>

    </div>

  </div>
</template>

<script>

import api from '@/api';
import settings from '@/settings';
import GoogleAddressAutocomplete from 'vue3-google-address-autocomplete';
import axios from 'axios';
import { fromZonedTime, toZonedTime, format as formatDate } from 'date-fns-tz';
import * as Sentry from '@sentry/vue';

export default {
  name: 'DraftBookingFlow_baseline',
  components: {
    GoogleAddressAutocomplete        
  },
  props: {
    draftBookingCode: {
      type: String,
      required: true
    },
    utmSource: {
      type: String,
      default: null
    },
    utmMedium: {
      type: String,
      default: null
    },
    utmCampaign: {
      type: String,
      default: null
    }
  },
  data: () => ({
    isLoaded: false,
    expires_at: null,
    stage: null,
    stepperStage: 1,
    loadError: null,
    updateUnderway: false,

    stageOrder: [
      'start',
      'personal',
      'treatment_category',
      'client_type',
      'treatment',
      'name',
      'preferred_dates',
      'number_of_people',
      'additional_info',
      'confirm_verification',
      'verification',
      'thank_you'
    ],

    draft_booking: {
      address_string: "",
      lat: null,
      lng: null,
      city: null,
      province: null,
      unit_number: "",
      phone_number: "",
      email: "",
      re_enter_email: "",
      primary_contact_method: "",
      treatment_category_id: null,
      client_type: null,
      treatment_id: null,
      first_name: "",
      last_name: "",
      scheduling_preferences_by_day: [],
      number_of_people: null,
      promo_code: "",
      client_notes: "",
      reason_for_visit: "",
      has_sent_verification: false,
      verification_code: "",
      agreed_to_terms: false,
      did_see_unsupported_treatment_category: false
    },
    is_ready_to_submit: false,
    is_invalid_verification_code: false,

    treatments: [],

    allowed_scheduling_preferences_per_day: [],
    selectable_preferred_service_dates: [],
    all_treatment_categories: [],
    allowed_treatment_categories: [],

    form_error: null,
    form_error_fields: [],
    showTreatmentCategoryNotAvailableOverlay: false,

    address_lookup: "",

    preferred_dates_is_add_mode: false,
    preferred_dates_add_date: null,
    preferred_dates_add_scheduling_preference: [],

    max_scheduling_preferences_per_day: 0,
    verification_code_length: 0,
    schedule_option_timezone: "",

    googleMapsApiKey: settings.googleMapsApiKey,
    isSpecificAddress: false,
  }),
  computed: {
    utm_source() {
      return this.utmSource;
    },
    utm_medium() {
      return this.utmMedium;
    },
    utm_campaign() {
      return this.utmCampaign;
    }
  },
  methods: {
    isFormFieldError(form_field) {
      return this.form_error_fields.indexOf(form_field) !== -1;
    },

    isValidEmailAddress(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },

    isValidPhoneNumber(phonenumber) {
      return /^\+1\d{10}$/.test(phonenumber);
    },

    scrubPhoneNumber(phonenumber) {
      if(this.isValidPhoneNumber(phonenumber)) {
        return phonenumber;
      }
      // If starts with "+1", remove the "+1"
      if(phonenumber.startsWith('+1')) {
        phonenumber = phonenumber.slice(2);
      // If starts with "1" (but not "+1"), remove the "1"
      } else if (phonenumber.startsWith('1') && phonenumber.length === 11) {
        phonenumber = phonenumber.slice(1);
      }
      phonenumber = phonenumber.replace(/\D/g, '');
      if(phonenumber.length === 10) {
        return "+1" + phonenumber;
      }
      return phonenumber;
    },

    async addressCallback (place) {
      if(!place) {
        return;
      }
      if (!place.geometry || !place.geometry.location || !place.address_components) {
        this.form_error = "Please select a more specific address";
        this.form_error_fields.push('address_string');
        this.address_lookup = '';
        this.updateValueInAddress();
        this.setTimerToClearFormFieldErrorFields();
        this.isSpecificAddress = false;
        return;
      }
      this.draft_booking.lat = place.geometry.location.lat();
      this.draft_booking.lng = place.geometry.location.lng();
      this.draft_booking.address_string = place.formatted_address;
      this.$nextTick(() => {          
        this.address_lookup = place.formatted_address;
        this.city = '';
        this.province = '';
        let hasStreetName = false;
        let hasStreetNumber = false;
        const addressComponents = place.address_components;
        for (let component of addressComponents) {
          if (component.types.includes('locality')) {
            this.draft_booking.city = component.long_name;
          }
          if (component.types.includes('administrative_area_level_1')) {
            this.draft_booking.province = component.long_name;
          }
          if (component.types.includes('route')) {
            hasStreetName = true;
          }
          if (component.types.includes('street_number')) {
            hasStreetNumber = true;
          }
        }
        if(!hasStreetName || !hasStreetNumber) {
          this.form_error = "Please select a more specific address";
          this.form_error_fields.push('address_string');
          this.address_lookup = '';
          this.updateValueInAddress();
          this.setTimerToClearFormFieldErrorFields();
          this.isSpecificAddress = false;
        } else {
          this.isSpecificAddress = true;
          this.updateValueInAddress();
        }
      });          
    },   

    getSelectedTreatmentCategoryDisplayName() {
      const selectedTreatmentCategory = this.all_treatment_categories.find(tc => tc.treatment_category_id === this.draft_booking.treatment_category_id);
      return selectedTreatmentCategory ? selectedTreatmentCategory.display_name : '';
    },
    
    updateValueInAddress() {
      if(!this.$refs.addressInput) {
        return;
      }
      this.$refs.addressInput.autocompleteInput.value = this.address_lookup;
    },

    setTimerToClearFormFieldErrorFields() {
      setTimeout(() => {
        this.form_error_fields = [];
      }, 5000);
    },

    async evaluateStageComplete() {
      this.form_error = null; 
      this.form_error_fields = [];
      
      if (this.stage === 'start') {
        if(!this.draft_booking.address_string || !this.isSpecificAddress) {
          this.form_error = "Required fields";            
          if (!this.draft_booking.address_string) {
            this.form_error_fields.push('address_string');
            this.setTimerToClearFormFieldErrorFields();
          }
          return false;
        } 
        return true;

      
      } else if (this.stage === 'personal') {

        if(!this.draft_booking.email || !this.draft_booking.re_enter_email) {
          this.form_error = "Required fields";            
          if (!this.draft_booking.email) {
            this.form_error_fields.push('email');
            this.setTimerToClearFormFieldErrorFields();
          }
          if (!this.draft_booking.re_enter_email) {
            this.form_error_fields.push('re_enter_email');
            this.setTimerToClearFormFieldErrorFields();
          }
          return false;
        }

        if(!this.isValidEmailAddress(this.draft_booking.email)) {
          this.form_error = "Invalid email";
          this.form_error_fields.push('email');
          this.setTimerToClearFormFieldErrorFields();
          return false;
        }

        if(this.draft_booking.email !== this.draft_booking.re_enter_email) {
          this.form_error = "Emails do not match";
          this.form_error_fields.push('email');
          this.form_error_fields.push('re_enter_email');
          this.setTimerToClearFormFieldErrorFields();
          return false;
        }

        return true;

      
      } else if (this.stage === 'treatment_category') {
        if(!this.draft_booking.treatment_category_id) {
          this.form_error = "Required fields";            
          this.form_error_fields.push('treatment_category_id');
          this.setTimerToClearFormFieldErrorFields();
          return false;
        } else if (!this.isSelectedTreatmentCategoryAvailable()) {
          this.draft_booking.did_see_unsupported_treatment_category = true;
          return false;
        }
        // User passed validation - set to false
        this.draft_booking.did_see_unsupported_treatment_category = false;
        return true;

      
      } else if (this.stage === 'client_type') {
        if(!this.draft_booking.client_type) {
          this.form_error = "Required fields";            
          this.form_error_fields.push('client_type');
          this.setTimerToClearFormFieldErrorFields();
          return false;
        }
        return true;

      
      } else if (this.stage === 'treatment') {
        if(!this.draft_booking.treatment_id) {
          this.form_error = "Required fields";            
          this.form_error_fields.push('treatment_id');
          this.setTimerToClearFormFieldErrorFields();
          return false;
        }
        return true;

      
      } else if (this.stage === 'name') {
        this.draft_booking.phone_number = this.scrubPhoneNumber(this.draft_booking.phone_number);
        if(!this.draft_booking.phone_number || !this.draft_booking.first_name || !this.draft_booking.last_name || !this.draft_booking.primary_contact_method) {
          this.form_error = "Required fields";            
          if (!this.draft_booking.first_name) {
            this.form_error_fields.push('first_name');
            this.setTimerToClearFormFieldErrorFields();
          }
          if (!this.draft_booking.last_name) {
            this.form_error_fields.push('last_name');
            this.setTimerToClearFormFieldErrorFields();
          }
          if (!this.draft_booking.phone_number) {
            this.form_error_fields.push('phone_number');
            this.setTimerToClearFormFieldErrorFields();
          }
          if (!this.draft_booking.primary_contact_method) {
            this.form_error_fields.push('primary_contact_method');
            this.setTimerToClearFormFieldErrorFields();
          }
          return false;
        }

        if(!this.isValidPhoneNumber(this.draft_booking.phone_number)) {
          this.form_error = "Invalid phone number";
          this.form_error_fields.push('phone_number');
          this.setTimerToClearFormFieldErrorFields();
          return false;
        }

        return true;

      
      } else if (this.stage === 'preferred_dates') {
        if(this.draft_booking.scheduling_preferences_by_day.length === 0) {
          this.form_error = "Please select at least one date";            
          return false;
        }

        for(let i = 0; i < this.draft_booking.scheduling_preferences_by_day.length; i++) {
          if(this.draft_booking.scheduling_preferences_by_day[i].scheduling_preferences.length === 0) {
            this.form_error = "Required fields";      
            this.form_error_fields.push('scheduling_preferences_by_day_' + i);      
            this.setTimerToClearFormFieldErrorFields();
            return false;
          }
        }

        return true;

      } else if (this.stage === 'number_of_people') {
        if(!this.draft_booking.number_of_people) {
          this.form_error = "Required fields";            
          this.form_error_fields.push('number_of_people');
          this.setTimerToClearFormFieldErrorFields();
          return false;
        }
        return true;

      } else if (this.stage === 'additional_info') {
        if(!this.draft_booking.reason_for_visit) {
          this.form_error = "Required fields";            
          this.form_error_fields.push('reason_for_visit');
          this.setTimerToClearFormFieldErrorFields();
          return false;
        }
        return true;
      } else if (this.stage === 'confirm_verification') {
        return true;

      } else if (this.stage === 'verification') {
        if(!this.draft_booking.verification_code) {
          this.form_error = "Required fields";            
          this.form_error_fields.push('verification_code');
          this.setTimerToClearFormFieldErrorFields();
          return false;
        }
        if(this.draft_booking.verification_code.length !== this.verification_code_length) {
          this.form_error = "Invalid verification code";            
          this.form_error_fields.push('verification_code');
          this.setTimerToClearFormFieldErrorFields();
          return false;
        }

        if(!this.draft_booking.agreed_to_terms) {
          this.form_error = "Please agree to the terms";            
          this.form_error_fields.push('agreed_to_terms');
          this.setTimerToClearFormFieldErrorFields();
          return false;
        }

        return true;          

      } else if (this.stage === 'thank_you') {
        return true;

      }

      this.form_error = "Unknown stage";
      return false;
    },

    reset() {
      this.address_lookup = "";
      this.draft_booking = {
        address_string: "",
        lat: null,
        lng: null,
        city: null,
        province: null,
        unit_number: "",
        phone_number: "",
        email: "",
        re_enter_email: "",
        primary_contact_method: null,
        treatment_category_id: null,
        client_type: null,
        treatment_id: null,
        first_name: "",
        last_name: "",
        scheduling_preferences_by_day: [],
        number_of_people: null,
        promo_code: "",
        client_notes: "",
        reason_for_visit: "",
        has_sent_verification: false,
        verification_code: "",
        agreed_to_terms: false,
        did_see_unsupported_treatment_category: false
      };

    },

    async saveDraftState(update_contact_info) {
      this.updateUnderway = true;
      let params = { ...this.draft_booking };
      if(update_contact_info == true) {
        params.update_contact_info = true;
      }
      params.utm_source = this.utm_source;
      params.utm_medium = this.utm_medium;
      params.utm_campaign = this.utm_campaign;
      params.stage = this.stage;
      try {
        let headers = await api.getApiHeaders();
        let response = await axios.put(api.getApiUrl('u/draft-booking/save/' + this.draftBookingCode), params, { headers });
        this.is_ready_to_submit = response.data.is_ready_to_submit;
        return true;
      } catch (error) {
        this.loadError = "Error saving draft state";
        
        // Log full error details to console
        console.error('Error saving draft booking:', {
          error: error,
          draftBookingCode: this.draftBookingCode,
          params: params,
          response: error.response ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers
          } : null
        });
        
        // Send to Sentry with context
        Sentry.captureException(error, {
          tags: {
            api_endpoint: 'draft-booking/save',
            draft_booking_code: this.draftBookingCode,
            stage: this.stage,
            variant: 'baseline'
          },
          extra: {
            request_params: params,
            response_status: error.response?.status,
            response_data: error.response?.data,
            utm_source: this.utm_source,
            utm_medium: this.utm_medium,
            utm_campaign: this.utm_campaign
          }
        });
      } finally {
        this.updateUnderway = false;
      }
    },

    async loadTreatmentCategories() {
      this.updateUnderway = true;
      let headers = await api.getApiHeaders();
      this.allowed_treatment_categories = [];
      let params = {
        lat: this.draft_booking.lat,
        lng: this.draft_booking.lng
      };
      try {
        let response = await axios.post(api.getApiUrl('u/draft-booking/treatment-categories/lookup'), params, { headers });
        this.allowed_treatment_categories = response.data.treatment_categories;
      } catch (error) {
        this.loadError = "Error loading treatment categories";
        console.error(error);
      } finally {
        this.updateUnderway = false;
      }
    },

    isSelectedTreatmentCategoryAvailable() {
      return this.allowed_treatment_categories.find(tc => tc.treatment_category_id === this.draft_booking.treatment_category_id);
    },

    async loadTreatments() {
      this.updateUnderway = true;
      let headers = await api.getApiHeaders();
      this.treatments = [];
      let params = {
        treatment_category_id: this.draft_booking.treatment_category_id,
        lat: this.draft_booking.lat,
        lng: this.draft_booking.lng,            
        client_type: this.draft_booking.client_type
      };
      try {
        let response = await axios.post(api.getApiUrl('u/draft-booking/treatments/lookup'), params, { headers });
        this.treatments = response.data.treatments;
      } catch (error) {
        console.error(error);
        this.loadError = "Error loading treatments";
      } finally {
        this.updateUnderway = false;
      }
    },

    async sendVerificationCode() {
      this.is_invalid_verification_code = false;
      this.updateUnderway = true;
      try {
        let headers = await api.getApiHeaders();
        await axios.post(api.getApiUrl('u/draft-booking/send-verification-code/' + this.draftBookingCode), {}, { headers });
        this.draft_booking.has_sent_verification = true;
      } catch (error) {
        console.error(error);
        this.loadError = "Error sending verification code";
      } finally {
        this.updateUnderway = false;
      }
    },

    async submitBooking() {
      this.updateUnderway = true;
      let params = null;
      try {
        let headers = await api.getApiHeaders();
        params = { 
          verification_code: this.draft_booking.verification_code,
          agreed_to_terms: this.draft_booking.agreed_to_terms
        };
        await axios.put(api.getApiUrl('u/draft-booking/submit/' + this.draftBookingCode), params, { headers });
      } catch (error) {
        // Log full error details to console
        console.error('Error submitting booking:', {
          error: error,
          draftBookingCode: this.draftBookingCode,
          params: params,
          response: error.response ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers
          } : null,
          draft_booking: this.draft_booking
        });
        
        // Send to Sentry with context
        Sentry.captureException(error, {
          tags: {
            api_endpoint: 'draft-booking/submit',
            draft_booking_code: this.draftBookingCode,
            response_status: error.response?.status?.toString(),
            variant: 'baseline'
          },
          extra: {
            request_params: params,
            response_status: error.response?.status,
            response_data: error.response?.data,
            verification_code_provided: !!this.draft_booking.verification_code,
            agreed_to_terms: this.draft_booking.agreed_to_terms,
            utm_source: this.utm_source,
            utm_medium: this.utm_medium,
            utm_campaign: this.utm_campaign,
            is_ready_to_submit: this.is_ready_to_submit
          }
        });
        
        throw error;
      } finally {
        this.updateUnderway = false;
      }
    },

    async goToNextStage() {

      this.is_invalid_verification_code = false;
      let result = await this.evaluateStageComplete();
      if(result !== true) {
        if(this.stage == 'treatment_category' && this.draft_booking.treatment_category_id && !this.isSelectedTreatmentCategoryAvailable()) {
          this.showTreatmentCategoryNotAvailableOverlay = true;
          this.draft_booking.did_see_unsupported_treatment_category = true;
          // Immediately save this flag to the server
          await this.saveDraftState(false);
        }
        return;
      }

      let update_contact_info = this.stage == 'name';

      try {
        await this.saveDraftState(update_contact_info);
      } catch (error) {
        this.form_error = "Error saving draft state";
        this.form_error_fields = [];
        return;
      }

      const currentStageIndex = this.stageOrder.indexOf(this.stage);
      if (currentStageIndex === -1) {
        console.error('Invalid stage');
        return;
      }

      let nextStage = this.stageOrder[currentStageIndex + 1];

      if(nextStage == 'treatment_category') {
        try {
          await this.loadTreatmentCategories();
        } catch (error) {
          console.error(error);
          this.form_error = "Error loading treatment categories";
          this.form_error_fields = [];
          return;
        }
      }

      if(nextStage == 'treatment') {
        try {
          await this.loadTreatments();
        } catch (error) {
          console.error(error);
          this.form_error = "Error loading treatments";
          this.form_error_fields = [];
          return;
        }
      }

      if(nextStage == 'number_of_people') {
        let treatment = this.treatments.find(t => t.treatment_id === this.draft_booking.treatment_id);
        if(treatment.treat_as_one_person == true) {
          this.draft_booking.number_of_people = 1;
          nextStage = this.stageOrder[currentStageIndex + 2];
        }
      }

      if(nextStage == 'verification') {
        try {
          await this.sendVerificationCode();
        } catch (error) {
          console.error(error);
          this.form_error = "Error sending verification code";
          this.form_error_fields = [];
          return;
        }
      }

      if(nextStage === 'thank_you') {
        this.is_invalid_verification_code = false;
        try {
          await this.submitBooking();
          // eslint-disable-next-line
          if (window.location.hostname == 'bookings.healthcasa.com' || window.location.hostname == 'bookings-qa.healthcasa.com') {
            console.log('booking_complete');
            // eslint-disable-next-line
            dataLayer.push({'event': 'booking_complete'});
          }

        } catch (error) {            
          if(error.response && error.response.data && error.response.data.detail && error.response.data.detail === 'Incorrect verification code') {
            this.form_error = "Incorrect verification code";
            this.is_invalid_verification_code = true;
          } else {
            console.error(error);
            this.form_error = "Error submitting booking";              
          }
          this.form_error_fields = [];
          return;
        }
        this.reset();
      }
      this.stage = nextStage;
      window.location.hash = `#stage_${nextStage}`;
    },
    hasGoBack() {
      return this.stageOrder.indexOf(this.stage) > 0;
    },
    async goToStart() {
      this.form_error = null;
      this.form_error_fields = [];
      this.is_invalid_verification_code = false;
      this.stage = 'start';
      window.location.hash = '#stage_start';
    },
    async goToPreviousStage() {
      if (this.hasGoBack()) {
        const currentStageIndex = this.stageOrder.indexOf(this.stage);
        const previousStage = this.stageOrder[currentStageIndex - 1];
        this.$nextTick(() => {  
          this.updateValueInAddress();
        });
        window.location.hash = `#stage_${previousStage}`;
      }
    },
    checkHash() {
      if(!this.draftBookingCode) {
        return;
      }

      const hash = window.location.hash;
      if (hash.startsWith('#stage_')) {
        const stage = hash.replace('#stage_', '');
        if (this.stageOrder.indexOf(stage) === -1) {
          console.error('Invalid stage');
          this.stage = 'start';
          window.location.hash = '#stage_start';
        } else {
          this.stage = stage;
          this.$nextTick(() => {  
            this.updateValueInAddress();
          });
        }
      } else {
        this.stage = 'start';        
      }
    },

    enablePreferredDatesAddDate() {
      this.preferred_dates_is_add_mode = true;
      this.form_error = null;
      this.form_error_fields = [];
    },

    canAddAnotherPreferredDate() {
      return this.draft_booking.scheduling_preferences_by_day.length < this.max_scheduling_preferences_per_day;
    },

    addPreferredDate() {
      if (!this.preferred_dates_add_date) {
        this.form_error = "Required fields";            
        return;
      }
      if (this.preferred_dates_add_scheduling_preference.length === 0) {
        this.form_error = "Required fields";            
        return;
      }

      if(this.draft_booking.scheduling_preferences_by_day.find(sp => sp.date === api.prepareDateOnly(this.preferred_dates_add_date))) {
        this.form_error = "Date already added";
        return;
      }

      let dt = toZonedTime(fromZonedTime(new Date( api.prepareDateOnly(this.preferred_dates_add_date)), this.schedule_option_timezone), 'UTC');
      let dt_format_nice = formatDate(dt, 'ccc, MMM d, yyyy');
      this.draft_booking.scheduling_preferences_by_day.push({
        date: api.prepareDateOnly(this.preferred_dates_add_date),
        date_format_nice: dt_format_nice,
        scheduling_preferences: this.preferred_dates_add_scheduling_preference
      });
      this.preferred_dates_add_date = null;
      this.preferred_dates_add_scheduling_preference = [];
      this.preferred_dates_is_add_mode = false;
      this.form_error = null;
      this.form_error_fields = [];
    },

    removePreferredDate(index) {
      this.draft_booking.scheduling_preferences_by_day.splice(index, 1);
    },

    async loadConfiguration() {
      this.updateUnderway = true;
      let headers = await api.getApiHeaders();
      try {
        let response = await axios.get(api.getApiUrl('u/draft-booking/configuration'), { headers });
        this.allowed_scheduling_preferences_per_day = response.data.configuration.allowed_scheduling_preferences_per_day;          
        this.max_scheduling_preferences_per_day = response.data.configuration.max_scheduling_preferences_per_day;
        this.selectable_preferred_service_dates = response.data.configuration.selectable_preferred_service_dates.map(d => {
          const date = new Date(d);
          const localDate = new Date(date.getTime() + (date.getTimezoneOffset() * 60000));
          return localDate;
        });
        this.verification_code_length = response.data.configuration.verification_code_length;
        this.schedule_option_timezone = response.data.configuration.schedule_option_timezone;
        this.all_treatment_categories = response.data.all_treatment_categories;
      } catch (error) {
        console.error(error);
        this.loadError = "Error loading configuration";
      } finally {
        this.updateUnderway = false;
      }
    },
    async startAgain() {
      this.reset();
      this.$emit('restart');
    },
    toggleSchedulingPreference(scheduling_preferences_by_day, allowed_scheduling_preference) {
      if(scheduling_preferences_by_day.scheduling_preferences.includes(allowed_scheduling_preference)) {
        scheduling_preferences_by_day.scheduling_preferences = scheduling_preferences_by_day.scheduling_preferences.filter(sp => sp !== allowed_scheduling_preference);
      } else {
        scheduling_preferences_by_day.scheduling_preferences.push(allowed_scheduling_preference);
      }
    },
    toggleSchedulingPreferenceAdd(allowed_scheduling_preference) {
      if(this.preferred_dates_add_scheduling_preference.includes(allowed_scheduling_preference)) {
        this.preferred_dates_add_scheduling_preference = this.preferred_dates_add_scheduling_preference.filter(sp => sp !== allowed_scheduling_preference);
      } else {
        this.preferred_dates_add_scheduling_preference.push(allowed_scheduling_preference);
      }
    }
  },
  created() {
    window.addEventListener('hashchange', this.checkHash);
  },    
  async mounted() {
    try {
      await this.loadConfiguration();        
      
      this.isLoaded = true;
      this.stage = 'start';
      window.location.hash = '#stage_start';
    } catch (error) {
      console.error(error);
      this.loadError = "Error loading";
    }
  },
  unmounted() {
    window.removeEventListener('hashchange', this.checkHash);
  },  
};
</script>
<style lang="scss" scoped>
/* Component-specific styles can go here if needed */
/* Most styles are in the parent HomeView.vue */
</style>

